import {
    TECHNIQUE_SWITCH_THRESHOLD_CM,
    isFixedPredesignSelection,
    isPredesignPrint,
} from "./designerHelpers";
import { MASTER_PRINT_TYPE } from "./designerUtils";

const PRINT_KIND = Object.freeze({
    TEXT: "text",
    IMAGE_UPLOAD: "image_upload",
    PREDESIGN: "predesign",
});

const PAPER_FORMATS = Object.freeze([
    { key: "A6", width: 10.5, height: 14.8 },
    { key: "A5", width: 14.8, height: 21.0 },
    { key: "A4", width: 21.0, height: 29.7 },
    { key: "A3", width: 29.7, height: 42.0 },
]);

const normalizeTruthyFlag = (value) => {
    const normalized = String(value ?? "").trim().toLowerCase();
    return normalized === "1"
        || normalized === "true"
        || normalized === "yes"
        || normalized === "on";
};

const parsePositiveNumber = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return parsed;
};

const stringifyIfPresent = (value) => {
    if (value === null || value === undefined || value === "") return null;
    return String(value);
};

const normalizePrintKind = ({ row = {}, attrs = {}, details = null } = {}) => {
    const explicit = String(attrs.print_kind || "").trim().toLowerCase();
    if (explicit === PRINT_KIND.TEXT || explicit === PRINT_KIND.IMAGE_UPLOAD || explicit === PRINT_KIND.PREDESIGN) {
        return explicit;
    }
    if (String(attrs.text || "").trim() || normalizeTruthyFlag(attrs.personal)) return PRINT_KIND.TEXT;
    if (String(attrs.image_url || "").trim()) return PRINT_KIND.IMAGE_UPLOAD;
    if (isPredesignPrint(details || row?.print || row)) return PRINT_KIND.PREDESIGN;
    return PRINT_KIND.IMAGE_UPLOAD;
};

const normalizeLegacyTechnique = (rawTechnique) => {
    const normalized = String(rawTechnique || "").trim().toLowerCase();
    if (normalized === "bordado") return "bordado";
    if (normalized === "aplique") return "aplique";
    if (
        normalized === "estampado"
        || normalized === "serigrafia"
        || normalized === "serigrafía"
        || normalized === "dtf"
    ) {
        return "estampado";
    }
    return "";
};

const resolveTechniqueFromPrintType = (masterPrintType) => {
    const type = Number(masterPrintType || 0);
    if (type === MASTER_PRINT_TYPE.PATCH) return "aplique";
    if (type === MASTER_PRINT_TYPE.SERIGRAPHY) return "estampado";
    if (type === MASTER_PRINT_TYPE.EMBROIDERY) return "bordado";
    return "";
};

const resolveMasterPrintTypeFromTechnique = (techniqueKey) => {
    if (techniqueKey === "aplique") return MASTER_PRINT_TYPE.PATCH;
    if (techniqueKey === "estampado") return MASTER_PRINT_TYPE.SERIGRAPHY;
    if (techniqueKey === "bordado") return MASTER_PRINT_TYPE.EMBROIDERY;
    return null;
};

const resolveRealDimensions = (attrs = {}, row = {}, details = null) => {
    const realWidth =
        parsePositiveNumber(attrs.real_width_cm)
        || parsePositiveNumber(attrs.rendered_width_cm)
        || parsePositiveNumber(attrs.width)
        || parsePositiveNumber(attrs.max_ancho)
        || parsePositiveNumber(attrs.maxw)
        || parsePositiveNumber(details?.max_width)
        || parsePositiveNumber(details?.max_ancho)
        || parsePositiveNumber(details?.maxw)
        || null;

    const realHeight =
        parsePositiveNumber(attrs.real_height_cm)
        || parsePositiveNumber(attrs.rendered_height_cm)
        || parsePositiveNumber(attrs.height)
        || parsePositiveNumber(attrs.base_height_cm)
        || parsePositiveNumber(details?.max_height)
        || parsePositiveNumber(details?.min_height)
        || null;

    return {
        realWidth,
        realHeight,
    };
};

const inferPaperFormat = (widthCm, heightCm) => {
    const width = parsePositiveNumber(widthCm);
    const height = parsePositiveNumber(heightCm);
    if (!width || !height) return null;

    const fitsFormat = (format) => (
        (width <= format.width && height <= format.height)
        || (width <= format.height && height <= format.width)
    );

    const match = PAPER_FORMATS.find(fitsFormat);
    return match?.key || null;
};

const inferLegacyTechnique = ({ attrs = {}, details = null, printKind = PRINT_KIND.IMAGE_UPLOAD, realWidth = null, realHeight = null } = {}) => {
    const explicitTechnique = normalizeLegacyTechnique(attrs.resolved_technique);
    if (explicitTechnique) return explicitTechnique;

    const typeBased = resolveTechniqueFromPrintType(attrs.resolved_master_print_type || details?.master_print_type);
    if (typeBased) return typeBased;

    if (printKind === PRINT_KIND.IMAGE_UPLOAD) return "estampado";
    if (printKind === PRINT_KIND.PREDESIGN) {
        return normalizeTruthyFlag(attrs.predesign_use_estampado) ? "estampado" : "bordado";
    }

    const sizeCm = parsePositiveNumber(realHeight) || parsePositiveNumber(realWidth) || 0;
    if (sizeCm > TECHNIQUE_SWITCH_THRESHOLD_CM) return "aplique";
    return "bordado";
};

const inferProductionMethod = ({
    attrs = {},
    printKind = PRINT_KIND.IMAGE_UPLOAD,
    legacyTechnique = "",
} = {}) => {
    const explicit = String(attrs.production_method || "").trim().toLowerCase();
    if (explicit) return explicit;

    if (normalizeTruthyFlag(attrs.is_mixed_technique) || String(attrs.production_recipe || "").trim()) {
        return "mixto";
    }

    if (printKind === PRINT_KIND.IMAGE_UPLOAD) return "dtf";
    if (printKind === PRINT_KIND.PREDESIGN && normalizeTruthyFlag(attrs.predesign_use_estampado)) return "dtf";
    if (legacyTechnique === "estampado") return "estampado";
    if (legacyTechnique === "aplique") return "aplique";
    if (legacyTechnique === "bordado") return "bordado";
    return "";
};

export const enrichPrintAttrsForSave = ({ row = {}, attrs = {}, details = null } = {}) => {
    const nextAttrs = { ...attrs };
    const looksLikePrintRow =
        Number(row?.master_print_id || 0) > 0
        || String(nextAttrs.location_key || "").trim() !== ""
        || String(nextAttrs.text || "").trim() !== ""
        || String(nextAttrs.image_url || "").trim() !== ""
        || String(nextAttrs.print_kind || "").trim() !== ""
        || String(nextAttrs.resolved_technique || "").trim() !== ""
        || Number(details?.master_print_type || row?.master_print_type || 0) > 0;

    if (!looksLikePrintRow) {
        return nextAttrs;
    }

    const printKind = normalizePrintKind({ row, attrs: nextAttrs, details });
    nextAttrs.print_kind = printKind;

    const { realWidth, realHeight } = resolveRealDimensions(nextAttrs, row, details);
    if (realWidth) {
        const normalizedWidth = realWidth.toFixed(1);
        nextAttrs.real_width_cm = stringifyIfPresent(nextAttrs.real_width_cm) || normalizedWidth;
        nextAttrs.rendered_width_cm = stringifyIfPresent(nextAttrs.rendered_width_cm) || normalizedWidth;
        nextAttrs.width = stringifyIfPresent(nextAttrs.width) || normalizedWidth;
    }
    if (realHeight) {
        const normalizedHeight = realHeight.toFixed(1);
        nextAttrs.real_height_cm = stringifyIfPresent(nextAttrs.real_height_cm) || normalizedHeight;
        nextAttrs.rendered_height_cm = stringifyIfPresent(nextAttrs.rendered_height_cm) || normalizedHeight;
        nextAttrs.height = stringifyIfPresent(nextAttrs.height) || normalizedHeight;
    }

    const fixedPredesign = String(nextAttrs?.free_scale || "") !== "1"
        && isFixedPredesignSelection({
            attrs: nextAttrs,
            print: details || row,
            mode: printKind === PRINT_KIND.PREDESIGN ? "predesign" : (printKind === PRINT_KIND.TEXT ? "text" : "image"),
        });
    if (fixedPredesign) {
        nextAttrs.is_fixed_size = "1";
    }

    const legacyTechnique = inferLegacyTechnique({
        attrs: nextAttrs,
        details,
        printKind,
        realWidth,
        realHeight,
    });
    if (legacyTechnique) {
        nextAttrs.resolved_technique = legacyTechnique;
        const resolvedMasterPrintType = resolveMasterPrintTypeFromTechnique(legacyTechnique);
        if (resolvedMasterPrintType) {
            nextAttrs.resolved_master_print_type = String(resolvedMasterPrintType);
        }
    }

    nextAttrs.technique_locked = "1";

    const productionMethod = inferProductionMethod({
        attrs: nextAttrs,
        printKind,
        legacyTechnique,
    });
    if (productionMethod) {
        nextAttrs.production_method = productionMethod;
    }

    const inferredPaperFormat = inferPaperFormat(realWidth, realHeight);
    if (fixedPredesign) {
        nextAttrs.size_system = "fixed";
    } else if (productionMethod === "dtf" && inferredPaperFormat) {
        nextAttrs.size_system = "paper_format";
        nextAttrs.size_format = inferredPaperFormat;
    } else {
        nextAttrs.size_system = "cm";
        delete nextAttrs.size_format;
    }

    const color1 = stringifyIfPresent(nextAttrs.color1 || nextAttrs.color);
    const color2 = stringifyIfPresent(nextAttrs.color2);

    if (productionMethod === "bordado") {
        if (color1) nextAttrs.thread_color = color1;
        nextAttrs.max_colors_used = "1";
    }

    if (productionMethod === "aplique") {
        if (color1) nextAttrs.fabric_color = color1;
        if (color2 || color1) nextAttrs.feston_color = color2 || color1;
        nextAttrs.max_colors_used = "2";
    }

    if (productionMethod === "dtf") {
        nextAttrs.full_color = "1";
        if (inferredPaperFormat) {
            nextAttrs.size_system = "paper_format";
            nextAttrs.size_format = inferredPaperFormat;
        }
        if (color1) nextAttrs.reference_color_1 = color1;
        if (color2 || color1) nextAttrs.reference_color_2 = color2 || color1;
    }

    if (normalizeTruthyFlag(nextAttrs.is_mixed_technique) || String(nextAttrs.production_recipe || "").trim()) {
        nextAttrs.is_mixed_technique = "1";
        if (!String(nextAttrs.production_method || "").trim()) {
            nextAttrs.production_method = "mixto";
        }
    }

    return nextAttrs;
};

export default enrichPrintAttrsForSave;
